import type { Dayjs } from "dayjs";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import errorImage from "@/assets/save-error.svg";
import pendingImage from "@/assets/save-manual.svg";
import successImage from "@/assets/save-success.svg";
import syncingImage from "@/assets/save-syncing.svg";
import { FloatButton } from "@/components";
import { ConfigurationManager } from "@/utils";

interface SyncingStatus {
    name: "pending" | "syncing" | "success" | "error";
    message?: string;
    lastChange: Dayjs;
    lastSync?: Dayjs;
    lastSavedIndex?: number;
}

type StatusProps = Record<
    SyncingStatus["name"],
    {
        background?: string;
        color: string;
        icon: string;
    }
>;

const Status: StatusProps = {
    pending: {
        background: "#0a99d5",
        color: "#fff",
        icon: pendingImage,
    },
    syncing: {
        color: "#0a99d5",
        icon: syncingImage,
    },
    success: {
        color: "#61bb46",
        icon: successImage,
    },
    error: {
        color: "#ee3123",
        icon: errorImage,
    },
};

const computeSyncingStatus = (
    syncingStatus: SyncingStatus,
    currentIndex: number,
    lastSavedIndex: number,
): SyncingStatus => {
    const status = { ...syncingStatus };
    if (currentIndex === lastSavedIndex) {
        status.name = "success";
    } else if (
        status.name !== "syncing" &&
        status.lastChange.isAfter(status.lastSync)
    ) {
        status.name = "pending";
    }
    return status;
};

/**
 * Props for the SaveChanges component.
 */
interface SaveChangesProps {
    /**
     * The current syncing status of the map.
     */
    syncingStatus: SyncingStatus;
    /**
     * Callback triggered when the user requests a save.
     */
    onSaveChanges: () => void;
    /**
     * The current position in the undo history.
     */
    currentIndex: number;
    /**
     * The history position of the last successful save.
     */
    lastSavedIndex: number;
    /**
     * Whether the save button is rendered at all.
     */
    showButton: boolean;
}

const SaveChangesComponent = ({
    syncingStatus,
    onSaveChanges,
    currentIndex,
    lastSavedIndex,
    showButton,
}: SaveChangesProps): ReactNode => {
    const currentStatus = useMemo(
        () => computeSyncingStatus(syncingStatus, currentIndex, lastSavedIndex),
        [syncingStatus, currentIndex, lastSavedIndex],
    );

    const { icon, color, background } = Status[currentStatus.name];

    return (
        showButton && (
            <FloatButton
                onClick={() =>
                    (currentStatus.name === "pending" ||
                        currentStatus.name === "error") &&
                    onSaveChanges()
                }
                message={currentStatus.message}
                color={color}
                icon={icon}
                backgroundColor={background}
            />
        )
    );
};

const mapStateToProps = ({ map, window }) => {
    const syncingStatus = map.general.syncingStatus;
    const { past } = map.values.global;
    return {
        syncingStatus,
        currentIndex: past.length + 1,
        lastSavedIndex: syncingStatus.lastSavedIndex,
        showButton:
            map.general.mode.name === "view" &&
            !window.loading.active &&
            !ConfigurationManager.values.readOnly,
    };
};

const mapDispatchToProps = {
    onSaveChanges: actions.map.general.saveChanges,
};

export const SaveChanges = connect(
    mapStateToProps,
    mapDispatchToProps,
)(SaveChangesComponent);
